import type { ReactElement } from "react";
import type { ApiClient, Konto, Vertragsart } from "@kundeninfo/api-client";
import { Feld } from "@kundeninfo/ui";
import { Auswahl, Schalter } from "@kundeninfo/ui/me-design";
import { Loaded, MasterDataPanel, MasterDataTable, StateFlags, useList, useMasterData } from "./masterData.js";
import { errorProp } from "../../../components/fieldErrors.js";
import { ACCOUNTS_TEXT as T } from "./texts.js";

/**
 * Verwaltung › VERTRAGSARTEN (Spec „Vertragsarten“, Entscheid 491): Vorgaben eines neuen Liefervertrags —
 * Abrechnungsweise, Rhythmus, Zahlweise und Vorgabekonto. Gepflegt wird das Vorgabekonto des Rahmens SKR04 (C11: nur
 * SKR04 gesät, alle Unternehmen auf SKR04); Einträge anderer Rahmen bleiben beim Speichern unverändert stehen.
 * Anlegen gibt es nicht (der Server kennt nur PUT), deaktivierte stehen in der Liste.
 */
export type ContractTypesClient = Pick<ApiClient, "listSupplierContractTypes" | "updateSupplierContractType"
  | "getBookingAccounts">;

const CHART = "SKR04";
type Accounts = Vertragsart["accounts"];
type ContractTypeDraft = Omit<Vertragsart, "typeKey"> & { account: string };

const accountOf = (accounts: Accounts): string => accounts.find((a) => a.chartOfAccounts === CHART)?.bookingAccountId ?? "";
const otherCharts = (accounts: Accounts): Accounts => accounts.filter((a) => a.chartOfAccounts !== CHART);
/** Feldname eines Fehlers am Vorgabekonto: Es steht hinter den Einträgen der anderen Rahmen (`save`). */
const accountField = (accounts: Accounts): string => `accounts.${otherCharts(accounts).length}.bookingAccountId`;

const toDraft = (v: Vertragsart | null): ContractTypeDraft => {
  const { typeKey: _key, ...rest } = v!; // Anlegen gibt es nicht — `open` bekommt immer eine Zeile.
  return { ...rest, account: accountOf(rest.accounts) };
};

const accountLabel = (k: Konto): string =>
  `${k.accountNumber} ${k.name}${k.active ? "" : ` (${T.inactive})`}`;

/** Eine Werteliste als Auswahl. */
function Choice<K extends string>(p: { label: string; value: K; texts: Readonly<Record<K, string>>; error: string | undefined;
  onChange: (k: K) => void }): ReactElement {
  return (
    <Auswahl beschriftung={p.label} wert={p.value} {...errorProp(p.error)} onGeaendert={(e) => p.onChange(e.wert as K)}>
      {(Object.keys(p.texts) as K[]).map((k) => <option key={k} value={k}>{p.texts[k]}</option>)}
    </Auswahl>
  );
}

export function ContractTypesSection({ client }: { client: ContractTypesClient }): ReactElement {
  const m = useMasterData(() => client.listSupplierContractTypes(), "", toDraft);
  const konten = useList(() => client.getBookingAccounts({ chartOfAccounts: CHART, kind: "EXPENSE", includeInactive: true }), "");
  // „–“ heißt „kein Vorgabekonto“; ein gesetztes Konto, das (noch) nicht geladen ist, zeigt den neutralen Platzhalter.
  const kontoText = (id: string): string => {
    const k = konten.rows?.find((x) => x.id === id);
    return id === "" ? T.none : k === undefined ? T.pending : accountLabel(k);
  };
  const d = m.draft;

  async function save(): Promise<void> {
    if (d === null) return;
    const { id, account, accounts, ...e } = d;
    await client.updateSupplierContractType(id, { ...e, accounts: [...otherCharts(accounts),
      ...(account === "" ? [] : [{ chartOfAccounts: CHART, bookingAccountId: account } as const])] });
    await m.reload();
  }

  return (
    <div className="vwk">
      <Loaded list={m}>{(rows) => (
        <MasterDataTable rows={rows} onEdit={m.open} editName={(v) => T.editNamed(v.name)}
          columns={[
            { head: T.name, cell: (v) => v.name },
            { head: T.billingMode, cell: (v) => T.billingModes[v.defaultBillingMode] },
            { head: T.cycle, cell: (v) => T.cycles[v.defaultCycle] },
            { head: T.paymentMethod, cell: (v) => T.paymentMethods[v.defaultPaymentMethod] },
            { head: T.defaultAccount(CHART), cell: (v) => kontoText(accountOf(v.accounts)) },
            { head: T.state, cell: (v) => <StateFlags row={v} /> },
          ]} />
      )}</Loaded>

      {d !== null && (
        <MasterDataPanel title={T.editContractType} kind={T.contractTypeKind} name={d.name} dirty={m.dirty} deactivating={m.deactivating} save={save} refresh={m.refresh}
          onClose={m.close} labels={{ name: T.name, defaultBillingMode: T.billingMode, defaultCycle: T.cycle,
            defaultPaymentMethod: T.paymentMethod, active: T.active, [accountField(d.accounts)]: T.defaultAccount(CHART) }}>
          {(f) => (
            <>
              <Feld id="vwk-name" label={T.name} bearbeitbar pflicht wert={d.name} fehler={f.name}
                onChange={(w) => m.change({ name: w })} />
              <Choice label={T.billingMode} value={d.defaultBillingMode} texts={T.billingModes} error={f.defaultBillingMode}
                onChange={(k) => m.change({ defaultBillingMode: k })} />
              <Choice label={T.cycle} value={d.defaultCycle} texts={T.cycles} error={f.defaultCycle}
                onChange={(k) => m.change({ defaultCycle: k })} />
              <Choice label={T.paymentMethod} value={d.defaultPaymentMethod} texts={T.paymentMethods}
                error={f.defaultPaymentMethod} onChange={(k) => m.change({ defaultPaymentMethod: k })} />
              <Auswahl beschriftung={T.defaultAccount(CHART)} wert={d.account} {...errorProp(f[accountField(d.accounts)])}
                onGeaendert={(e) => m.change({ account: e.wert })}>
                <option value="">{T.none}</option>
                {(konten.rows ?? []).filter((k: Konto) => k.active || k.id === d.account)
                  .map((k) => <option key={k.id} value={k.id}>{accountLabel(k)}</option>)}
              </Auswahl>
              <Schalter beschriftung={T.active} an={d.active} {...errorProp(f.active)}
                onGeaendert={(e) => m.change({ active: e.an })} />
            </>
          )}
        </MasterDataPanel>
      )}
    </div>
  );
}
